/**
 * The smallest template engine the event system needs: it puts values into
 * `{{name}}` references and does nothing else.
 *
 * This is the twin of `server/internal/tmpl` and the two must agree byte
 * for byte. `server/internal/tmpl/testdata/tmpl-vectors.json` is the golden
 * fixture that holds them together (the R72/R104 pattern); `tmpl.test.ts`
 * asserts this side against it.
 *
 * Rules:
 * - A reference is `{{name}}`, where name is one or more characters from
 *   `[A-Za-z0-9_]`. There are no spaces inside the braces.
 * - A reference whose name is not in `vars` renders literally, braces and
 *   all. Malformed braces render literally too: `{{` with no `}}` after it,
 *   or a name with any other character in it. A visible bug is better than
 *   a message that silently loses a word.
 * - There is no escaping of any kind. The output is plain text. A caller
 *   that puts the output into the DOM must escape it there, which is what
 *   the toast (`textContent`) and the inbox panel (`esc()`) already do.
 */

const REF_OPEN = '{{';
const REF_CLOSE = '}}';

/** A legal reference name: one or more of `[A-Za-z0-9_]`, and nothing else. */
const NAME = /^[A-Za-z0-9_]+$/;

/**
 * Replaces every `{{name}}` reference in `t` with the `vars` entry of that
 * name. A reference with no entry, and any malformed brace pair, is copied
 * to the output as it stands.
 */
export function render(t: string, vars: Record<string, string>): string {
  if (!t.includes(REF_OPEN)) return t;
  let out = '';
  let rest = t;
  for (;;) {
    const i = rest.indexOf(REF_OPEN);
    if (i < 0) return out + rest;
    out += rest.slice(0, i);
    const after = rest.slice(i + REF_OPEN.length);
    const j = after.indexOf(REF_CLOSE);
    // No closing braces at all: the remainder is literal text.
    if (j < 0) return out + rest.slice(i);
    const name = after.slice(0, j);
    if (NAME.test(name) && Object.prototype.hasOwnProperty.call(vars, name)) {
      out += vars[name];
    } else {
      out += rest.slice(i, i + REF_OPEN.length + j + REF_CLOSE.length);
    }
    rest = after.slice(j + REF_CLOSE.length);
  }
}
